/**
 * The editor route's own logic: load the template into the store, save it back, and own the one
 * `useValidateDefinition` instance the shell shares.
 *
 *  - Validation runs on save and on demand, never on keystroke (§A) — there is deliberately no
 *    `watch` here that calls `validate()`.
 *  - The save payload is the working document, never Zod's output (B9). `buildSavePayload()` is an
 *    action rather than a getter because it also reconciles the media manifest (B13.11).
 *  - `applySavedTemplate` is what clears `isDirty` — the response is the new pristine.
 *
 * And one router fact this file is built around: Vue Router reuses the component instance when only
 * a dynamic segment changes, so `/content/assessments/12` → `/13` re-runs neither `onMounted` nor
 * `onBeforeRouteLeave`. Without the `watch` below, the store would still hold the old template and
 * the next save would `PATCH` the wrong row (a full replacement, §4). Duplicate is that navigation.
 */
import { computed, ref, watch } from 'vue'
import { useEventListener } from '@vueuse/core'

import type { ComputedRef, Ref } from 'vue'
import type {
  AssessmentValidationBlocker,
  AssessmentValidationGroup,
  AssessmentValidationIssue,
} from './interfaces'

import { useToast } from '@shared/ui/toast'

import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import { useAssessmentTemplateShowApi } from '../../api/assessment-templates/show.hook'
import { useAssessmentTemplateUpdateApi } from '../../api/assessment-templates/update.hook'
import { useAssessmentTemplateDuplicateApi } from '../../api/assessment-templates/duplicate.hook'
import { AssessmentTemplateStatus } from './enums'
import { useAssessmentEditorStore } from './store'
import { useValidateDefinition } from './validate-definition.hook'
import { useValidationReport } from './validation-report.hook'
import { useAssessmentPreviewModal } from './assessment-preview-modal.hook'
import {
  getAssessmentEditPath,
  TEMPLATE_TITLE_MAX_LENGTH,
} from './assessment-template-options'

const LEAVE_CONFIRMATION = 'This assessment template has unsaved changes. Leave and discard them?'
/** The API's own wording (§4 `required|max:255`), said before the request rather than after it. */
const TITLE_REQUIRED_MESSAGE = 'Title is required.'
const TITLE_TOO_LONG_MESSAGE = `Title must not exceed ${TEMPLATE_TITLE_MAX_LENGTH} characters.`

export interface AssessmentEditorPageReturn {
  isLoading: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  isDirty: ComputedRef<boolean>
  isNotFound: Ref<boolean>
  hasDocument: ComputedRef<boolean>
  issues: Ref<AssessmentValidationIssue[]>
  blockers: ComputedRef<AssessmentValidationBlocker[]>
  groups: ComputedRef<AssessmentValidationGroup[]>
  summary: ComputedRef<string | null>
  /** Validated, and something was found — separates "nothing to say" from "all clear". */
  hasProblems: ComputedRef<boolean>
  hasValidated: Ref<boolean>
  /** `null` unless the list is already in the store — see the report's attempts-count gap. */
  attemptsCount: ComputedRef<number | null>
  /** The row's lifecycle status, or `null` before a template is loaded. */
  status: ComputedRef<AssessmentTemplateStatus | null>
  isPublished: ComputedRef<boolean>
  load: () => Promise<void>
  save: () => Promise<void>
  /** Save AND move to `PUBLISHED` — same validation gate as `save`, one request. */
  publish: () => Promise<void>
  duplicate: () => Promise<void>
  validate: () => void
  /** `false` when the issue is document-level and there is nothing to select. */
  focusIssue: (issue: AssessmentValidationIssue) => boolean
  /** The route guard: `true` to allow the navigation, `false` to cancel it. */
  confirmLeave: () => boolean
  /** The single cleanup path — the page calls it from `onUnmounted` and nowhere else. */
  leave: () => void
}

/** The route param is the numeric row id. Anything else is rejected before a request is made,
 *  rather than sent to the API to 404. */
const toTemplateId = (raw: unknown): number | null => {
  const value = Array.isArray(raw) ? raw[0] : raw
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return null
  }
  const id = Number(value)
  return id > 0 ? id : null
}

export const useAssessmentEditorPage = (): AssessmentEditorPageReturn => {
  const route = useRoute()
  const router = useRouter()
  const store = useAssessmentEditorStore()
  const templatesStore = useAssessmentTemplatesStore()
  const { closePreview } = useAssessmentPreviewModal()
  const toast = useToast()

  const isNotFound = ref<boolean>(false)

  const templateId = computed<number | null>(() => toTemplateId(route.params.id))

  const {
    issues,
    hasValidated,
    canSave,
    validate: runValidate,
    reset: resetValidation,
  } = useValidateDefinition(() => store.getWorkingDocument)

  // The presentation half. Needs the RAW document: a document with issues never parsed.
  const {
    refBlockers: blockers,
    refGroups: groups,
    refSummary: reportSummary,
    refHasProblems: hasProblems,
    resolveTarget,
  } = useValidationReport(issues, () => store.getWorkingDocument)

  /** The report's `null` covers three states — not run, clean, and blockers-only — so the all-clear
   *  line is stated only when nothing at all was found. */
  const summary = computed<string | null>(() => {
    if (!hasValidated.value) {
      return null
    }
    if (reportSummary.value !== null) {
      return reportSummary.value
    }
    return hasProblems.value ? null : 'No problems found'
  })

  const isLoading = computed<boolean>(() => store.isLoading)
  const isSaving = computed<boolean>(() => store.isSaving)
  const isDirty = computed<boolean>(() => store.isDirty)
  const hasDocument = computed<boolean>(() => store.hasDocument)
  const status = computed<AssessmentTemplateStatus | null>(() => store.getStatus)
  const isPublished = computed<boolean>(() => store.getStatus === AssessmentTemplateStatus.Published)

  /**
   * `show` returns no count — `assessments_count` exists on the LIST ROW only. So the number is
   * honest exactly when the admin arrived from the list; on a deep link it is `null` and no
   * warning is rendered, since one reading "0 attempts" would be worse than none.
   */
  const attemptsCount = computed<number | null>(() => {
    const id = templateId.value
    if (id === null) {
      return null
    }
    const row = templatesStore.getTemplates.find(template => template.id === id)
    return row ? row.assessments_count : null
  })

  const load = async (): Promise<void> => {
    resetValidation()

    if (templateId.value === null) {
      isNotFound.value = true
      return
    }

    isNotFound.value = false
    store.setLoading(true)

    // The show hook toasts its own failure and resolves to `null`, so an unknown id is a rendered
    // empty state rather than an exception or a blank screen.
    const template = await useAssessmentTemplateShowApi({ templateId: templateId.value })

    if (!template) {
      isNotFound.value = true
      store.setLoading(false)
      return
    }

    store.loadTemplate(template)
    store.setLoading(false)
  }

  /** The on-demand trigger the toolbar's "Validate" button uses. */
  const validate = (): void => {
    runValidate()
  }

  /** Selection, not scrolling: the form column is the only place a selected question is rendered,
   *  so selecting it is revealing it. A rubric resolves through the question referencing it. */
  const focusIssue = (issue: AssessmentValidationIssue): boolean => {
    const target = resolveTarget(issue)
    if (!target?.screenId) {
      return false
    }
    store.selectScreen(target.screenId)
    if (target.activityId) {
      store.selectActivity(target.activityId)
    }
    return true
  }

  /**
   * The row title, trimmed into the store and held to the API's rule before any request is made.
   * `useValidateDefinition` only ever sees the DOCUMENT, so without this the one row field the
   * editor can edit would be checked nowhere and come back a 422 (§4).
   *
   * Trimmed HERE rather than on keystroke, so that a trailing space stays typeable — and trimmed
   * INTO THE STORE rather than only into the payload, or `applySavedTemplate` would compare the
   * saved title against an untrimmed one and report a clean template as still dirty.
   */
  const commitTitle = (): boolean => {
    const trimmed = store.getTitle.trim()

    if (trimmed !== store.getTitle) {
      store.setTitle(trimmed)
    }

    if (trimmed.length === 0) {
      toast.error(TITLE_REQUIRED_MESSAGE)
      return false
    }

    if (trimmed.length > TEMPLATE_TITLE_MAX_LENGTH) {
      toast.error(TITLE_TOO_LONG_MESSAGE)
      return false
    }

    return true
  }

  /**
   * Validate-then-save, deliberately in that order: a permanently disabled Save on a
   * never-validated document is a dead end. Pressing it validates first and only saves a clean
   * document; a dirty one leaves its issues on screen and makes no request.
   *
   * The document is checked before the title so that the report on screen still comes from the
   * document — the title is one field the author is looking at, and a toast is enough for it.
   *
   * `status` is the one optional key of the full replacement (RND-76). Left out, the row keeps
   * its status — an ordinary Save never moves a template between Draft / Published / Archived.
   * Passed, it rides along with the document in the same request, so a Publish can never land a
   * document the validator has not seen.
   */
  const persist = async (status?: AssessmentTemplateStatus): Promise<void> => {
    runValidate()

    if (!canSave.value) {
      return
    }

    if (!commitTitle()) {
      return
    }

    const id = store.getTemplateId
    // `buildSavePayload`, never `getPayload`: it reconciles the media manifest and returns the
    // body in one call, so no save path can forget a step (B13.11).
    const payload = store.buildSavePayload()
    if (id === null || !payload) {
      return
    }

    store.setSaving(true)
    const saved = await useAssessmentTemplateUpdateApi({
      templateId: id,
      payload: status ? { ...payload, status } : payload,
    })
    if (saved) {
      // The response is the new pristine — this is what clears `isDirty` (and writes `status`).
      store.applySavedTemplate(saved)
    }
    store.setSaving(false)
  }

  const save = async (): Promise<void> => {
    await persist()
  }

  /** The only way a template becomes visible to tutors. Gated exactly like Save. */
  const publish = async (): Promise<void> => {
    await persist(AssessmentTemplateStatus.Published)
  }

  /**
   * The copy is seated in the store BEFORE the navigation. `duplicate` already returns the full
   * new row (§6), so re-fetching would be redundant and would leave a window in which the URL says
   * one id and the store holds another. The `watch` below then sees it loaded and skips the
   * reload; it remains the safety net for every other way the param can change.
   */
  const duplicate = async (): Promise<void> => {
    const id = store.getTemplateId
    if (id === null) {
      return
    }
    const created = await useAssessmentTemplateDuplicateApi({ templateId: id })
    if (!created) {
      return
    }
    resetValidation()
    store.loadTemplate(created)
    isNotFound.value = false
    await router.push(getAssessmentEditPath(created.id))
  }

  const leave = (): void => {
    store.clear()
    resetValidation()
  }

  /**
   * Re-seat the editor when the route's id changes under a reused component instance. Skipped when
   * the store already holds that template — the duplicate path, seated before the navigation.
   */
  watch(templateId, async (next) => {
    // BEFORE the early return, and unconditionally: this watch is the only signal that the
    // document changed under a reused component instance, so the preview modal's own
    // `onScopeDispose` never fires. Without it, Duplicate with the preview open left the modal up
    // over the copy — and the early return below is precisely that path.
    closePreview()

    if (next !== null && store.getTemplateId === next) {
      return
    }
    store.clear()
    await load()
  })

  /**
   * Unsaved authoring work is expensive to recreate, so leaving must be deliberate. This guards
   * ROUTE LEAVES ONLY: a same-route param change goes through the `watch` above and is not blocked
   * — by then the decision has been made (duplicate), and a stale dirty flag must never strand the
   * editor on an id the URL has moved past.
   */
  const confirmLeave = (): boolean => {
    if (!store.isDirty) {
      return true
    }
    return typeof globalThis.confirm === 'function'
      ? globalThis.confirm(LEAVE_CONFIRMATION)
      : true
  }

  // Tab close / reload — the router never sees these. `useEventListener` removes the listener on
  // scope dispose, i.e. when the editor unmounts.
  useEventListener(globalThis, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!store.isDirty) {
      return
    }
    event.preventDefault()
    // Browsers ignore custom text and show their own wording; the assignment is what still
    // triggers the prompt in older engines.
    event.returnValue = ''
  })

  return {
    isLoading,
    isSaving,
    isDirty,
    isNotFound,
    hasDocument,
    issues,
    blockers,
    groups,
    summary,
    hasProblems,
    hasValidated,
    attemptsCount,
    status,
    isPublished,
    load,
    save,
    publish,
    duplicate,
    validate,
    focusIssue,
    confirmLeave,
    leave,
  }
}
